import { Injectable, inject, Injector } from '@angular/core';
import { LatLng } from 'leaflet';
import { WmsFieldMappingHandler, TransformationService } from '@luzula/luzula-lib';
import { AufnahmePunktDocument, istLeer } from '../../data-model';
import type { DataService } from '../../data.service';

/**
 * Handler für das WMS-Feld-Mapping bei AufnahmePunktDocument.
 * Ermöglicht dem WmsDataService, Dokumente zu befüllen ohne das Datenmodell zu kennen.
 */
@Injectable({ providedIn: 'root' })
export class BohrpunktWmsFieldMappingHandler implements WmsFieldMappingHandler<AufnahmePunktDocument> {
	private ts = inject(TransformationService);
	private injector = inject(Injector);

	/**
	 * Liefert die Koordinaten für WMS-Abfragen.
	 */
	getCoordinates(bp: AufnahmePunktDocument): LatLng {
		return this.ts.from(bp.header.koordinaten);
	}

	/**
	 * Prüft ob Klimastufe abgefragt werden soll (wenn neu-Feld leer).
	 */
	shouldFetchKlimastufe(bp: AufnahmePunktDocument): boolean {
		return istLeer(bp.extendedHeader?.klimaStufe?.neu);
	}

	/**
	 * Setzt die Klimastufe-Daten.
	 */
	setKlimastufeData(bp: AufnahmePunktDocument, data: { klimastufe?: string }): void {
		if (!bp.extendedHeader) return;
		if (!bp.extendedHeader.klimaStufe) {
			bp.extendedHeader.klimaStufe = { neu: '' };
		}
		bp.extendedHeader.klimaStufe.neu = data.klimastufe as any || '';
	}

	/**
	 * Prüft ob Forstadresse abgefragt werden soll: wenn einer der beiden Blöcke
	 * leer ist (Forstamt mit Revier, Abteilung mit Unterabteilung und
	 * Teilfläche). Bis zum 02.10.2026 zählte nur die Abteilung; stand sie schon
	 * da, blieben Forstamt und Revier für immer leer.
	 */
	shouldFetchForstadresse(bp: AufnahmePunktDocument): boolean {
		return this.forstBlockLeer(bp) || istLeer(bp.header?.adresse?.abteilung);
	}

	/**
	 * Setzt die Forstadresse-Daten. Ohne `ueberschreiben` wird nur ein leerer
	 * Block gefüllt, und zwar ganz oder gar nicht: Die Antwort kann Sekunden nach
	 * der Frage kommen, und eine geholte Unterabteilung neben einer von Hand
	 * eingetragenen Abteilung ergäbe eine Adresse, die es nicht gibt. Eine leere
	 * Antwort (Punkt ausserhalb der Forstgrundkarte) ändert nichts.
	 *
	 * Mit `ueberschreiben` (Punkt verschoben) wird alles ersetzt, auch durch
	 * Leeres: Die alte Adresse gilt am neuen Ort nicht.
	 */
	setForstadresseData(bp: AufnahmePunktDocument, data: {
		abt?: string;
		ua?: string;
		tf?: string;
		fa_txt?: string;
		fr_txt?: string;
		schl_gis?: string;
		luzulaServerFehlt?: boolean;
	}, ueberschreiben = false): void {
		if (!bp.header?.adresse) return;
		// Teilfläche: direkt aus data.tf (BB/FGK) oder aus schl_gis extrahiert (MV/FGK)
		const tf = data.tf || data.schl_gis?.match(/_[a-z]_(\d+)_/i)?.[1] || '';
		if (ueberschreiben || (istLeer(bp.header.adresse.abteilung) && data.abt)) {
			bp.header.adresse.abteilung = data.abt || '';
			bp.header.adresse.unterabteilung = data.ua || '';
			bp.header.adresse.teilflaeche = tf;
		}
		if (ueberschreiben || (this.forstBlockLeer(bp) && (data.fa_txt || data.fr_txt))) {
			// Forstamt/Revier mit Nummern setzen (z.B. "06 Neustrelitz", "04 Theerofen")
			bp.header.forstamt = this.withNr(data.fa_txt, data.fr_txt, 'FORSTAMT') || data.fa_txt || '';
			bp.header.revier = this.withNr(data.fa_txt, data.fr_txt, 'FORSTREVIE') || data.fr_txt || '';
		}
	}

	private forstBlockLeer(bp: AufnahmePunktDocument): boolean {
		return istLeer(bp.header?.forstamt) && istLeer(bp.header?.revier);
	}

	/**
	 * Prüft ob Katasteradresse abgefragt werden soll (wenn Gemarkung leer).
	 */
	shouldFetchKatasteradresse(bp: AufnahmePunktDocument): boolean {
		return istLeer(bp.header?.adresse?.gemarkung);
	}

	/**
	 * Setzt die Katasteradresse-Daten.
	 */
	setKatasteradresseData(bp: AufnahmePunktDocument, data: {
		gmkname?: string;
		flurnr?: string;
		'cp:label'?: string;
	}): void {
		if (!bp.header?.adresse) return;
		bp.header.adresse.gemarkung = data.gmkname || '';
		// Flur aus flurnr: letzte 3 Ziffern als int (z.B. "134162002" -> 2)
		if (data.flurnr && data.flurnr.length >= 3) {
			const flurNum = parseInt(data.flurnr.slice(-3), 10);
			if (!isNaN(flurNum)) {
				bp.header.adresse.flur = flurNum;
			}
		}
		// Flurstück aus cp:label
		if (data['cp:label']) {
			bp.header.adresse.flurstueck = data['cp:label'];
		}
	}

	/**
	 * Prüft ob Wuchsgebiet abgefragt werden soll (wenn Wuchsgebiet leer).
	 */
	shouldFetchWuchsgebiet(bp: AufnahmePunktDocument): boolean {
		return istLeer(bp.extendedHeader?.wuchsgebiet);
	}

	/**
	 * Setzt die Wuchsgebiet-Daten.
	 */
	setWuchsgebietData(bp: AufnahmePunktDocument, data: { bez_wg_bu?: string; bez_wb_bu?: string }): void {
		if (!bp.extendedHeader) return;
		bp.extendedHeader.wuchsgebiet = data.bez_wg_bu || '';
		bp.extendedHeader.wuchsbezirk = data.bez_wb_bu || '';
	}

	/** Schlägt Nummer+Name aus Reviere-Tabelle nach (z.B. "06 Neustrelitz") */
	private withNr(faTxt: string | undefined, frTxt: string | undefined, col: 'FORSTAMT' | 'FORSTREVIE'): string {
		if (!faTxt || !frTxt) return '';
		try {
			const { DataService } = require('../../data.service');
			const ds = this.injector.get(DataService) as DataService;
			const reviere = ds.getHardcoded('Reviere') as any[];
			if (!reviere) return '';
			const entry = reviere.find((r: any) => r.FA_TXT === faTxt && r.FR_TXT === frTxt);
			return entry ? entry[col] : '';
		} catch { return ''; }
	}

	/**
	 * Speichert das Dokument nach WMS-Datenanreicherung in die DB.
	 * Nur für bereits gespeicherte Punkte (mit _id und _rev).
	 *
	 * Liefert einen Promise, der nach dem Save resolved — der Einzelpunkt-Pfad
	 * im WmsDataService ignoriert ihn (fire-and-forget), die Batch-Variante
	 * `fetchWmsDataForDocumentsSequential` wartet darauf.
	 */
	async saveDocument(bp: AufnahmePunktDocument): Promise<void> {
		if (!(bp as any)._id || !(bp as any)._rev) return;
		// Lazy resolve um zirkuläre Abhängigkeit DataService → Handler → DataService zu vermeiden
		const m = await import('../../data.service');
		const ds = this.injector.get(m.DataService) as DataService;
		try {
			await ds.saveDocumentQuiet(bp);
		} catch (err: any) {
			console.warn('WMS Auto-Save fehlgeschlagen:', err);
		}
	}
}
